import React, { useState } from "react";
import { HelpCircle, X, FileDown, Info } from "lucide-react";

// Modal explaining the two acceptable CSV formats for photographer
// import. Kept next to the Import button so admins can peek at it
// before they upload a badly-shaped roster and hit a 400.
//
// The template files are inlined so we don't need a backend round-trip
// (and they Just Work offline / on preview). Both templates match the
// current importer exactly.
const TEMPLATE_SINGLE_NAME = `Name,photographer_code,email,membership_number
Danny Godfrey,127,dan@example.com,MEM-127
John George Doo,143,,
S Green,,stu@example.com,
Bill Metson LRPS,201,,`;

const TEMPLATE_TWO_COLUMN = `first_name,last_name,photographer_code,email,membership_number,active_status
Danny,Godfrey,127,dan@example.com,MEM-127,true
John George,Doo,143,,,true
Stuart,Green,,stu@example.com,,true`;

function DownloadTemplate({ label, filename, body }) {
  const download = () => {
    const blob = new Blob([body], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  return (
    <button
      onClick={download}
      className="ccs-btn-ghost text-xs flex items-center gap-2"
      data-testid={`download-template-${filename}`}
    >
      <FileDown size={13} /> {label}
    </button>
  );
}

export function ImportPhotographersHelp() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="ccs-btn-ghost inline-flex items-center gap-1 text-xs"
        data-testid="import-help-button"
        title="How should my CSV be formatted?"
      >
        <HelpCircle size={14} /> Format help
      </button>

      {open && (
        <div className="fixed inset-0 z-40 flex items-start justify-center bg-black/70 overflow-y-auto py-10">
          <div className="ccs-card w-full max-w-2xl mx-4" data-testid="import-help-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-800">
              <h2 className="text-lg font-medium flex items-center gap-2">
                <Info size={16} className="text-amber-400" /> Photographer CSV format
              </h2>
              <button
                onClick={() => setOpen(false)}
                className="p-1 hover:text-amber-400"
                data-testid="import-help-close"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            <div className="px-6 py-5 space-y-6 text-sm">
              <p>
                Your file must be a UTF-8 <code>.csv</code>. The importer detects the
                header row automatically — you can have a title or blank rows above it.
                Column order does not matter, and header names are case-insensitive.
              </p>

              <div>
                <div className="ccs-label mb-1">Option A — one Name column</div>
                <p className="muted-text text-xs mb-2">
                  Simplest for rosters exported from a spreadsheet. We split each
                  name into first + last automatically (last word = surname; commas
                  and honorifics like <em>LRPS/ARPS/EFIAP/BPE3*</em> are handled).
                </p>
                <pre className="mono text-xs p-3 overflow-x-auto"
                     style={{ background: "#0f0f0f", border: "1px solid #2a2a2a" }}>
{TEMPLATE_SINGLE_NAME}
                </pre>
                <div className="mt-2">
                  <DownloadTemplate
                    label="Download this template"
                    filename="photographers-template-single-name.csv"
                    body={TEMPLATE_SINGLE_NAME}
                  />
                </div>
              </div>

              <div>
                <div className="ccs-label mb-1">Option B — separate First and Last columns</div>
                <p className="muted-text text-xs mb-2">
                  Best if you want fine control over how a name splits (e.g. keep
                  &ldquo;van der Berg&rdquo; as one surname). Accepted aliases include
                  <code> Name_First </code>/<code> Name_Last</code>, <code> Given </code>/<code> Surname</code>,
                  <code> Forename </code>/<code> Family Name</code>.
                </p>
                <pre className="mono text-xs p-3 overflow-x-auto"
                     style={{ background: "#0f0f0f", border: "1px solid #2a2a2a" }}>
{TEMPLATE_TWO_COLUMN}
                </pre>
                <div className="mt-2">
                  <DownloadTemplate
                    label="Download this template"
                    filename="photographers-template-first-last.csv"
                    body={TEMPLATE_TWO_COLUMN}
                  />
                </div>
              </div>

              <div>
                <div className="ccs-label mb-1">Optional columns (both layouts)</div>
                <ul className="list-disc pl-5 text-xs space-y-1 muted-text">
                  <li><code>photographer_code</code> — the I.D. printed on scoresheets. Blank = auto-assigned.</li>
                  <li><code>email</code> — used for password-reset invitations. Blank = fine.</li>
                  <li><code>membership_number</code> — free-text (e.g. <code>MEM-127</code>).</li>
                  <li><code>tel_no</code> — phone number.</li>
                  <li><code>active_status</code> — <code>true</code> / <code>false</code>. Defaults to <code>true</code>.</li>
                  <li><code>enters_competitions</code> — <code>true</code> / <code>false</code>. Defaults to <code>true</code>.</li>
                </ul>
              </div>

              <div className="p-3 text-xs flex gap-2"
                   style={{ background: "#0a1f0a", border: "1px solid #2b5c2b", color: "#8fdc8f" }}>
                <Info size={14} className="mt-0.5 shrink-0" />
                <div>
                  <b>Safe to re-run.</b> Uploading the same file twice will not
                  duplicate rows — matching photographers are updated (email
                  filled in if missing) and identical rows are left alone.
                  If you spot duplicates from typos, use <b>Merge duplicates</b>
                  after import.
                </div>
              </div>
            </div>

            <div className="flex justify-end px-6 py-4 border-t border-neutral-800">
              <button
                onClick={() => setOpen(false)}
                className="ccs-btn"
                data-testid="import-help-done"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
